import React, { useCallback, useEffect, useRef } from "react";

const CONTENT_PAD = 26;
const CROWN_HALF_RATIO = 0.25;
const CONTENT_CENTER_VAR = "--gcc-header-content-center";

function getCrownHalfBounds(headerEl) {
  const W = headerEl.clientWidth;
  const isMobile = headerEl.clientHeight <= 72;

  if (isMobile) {
    return { min: 80, max: 140 };
  }
  if (W < 1024) {
    return { min: 50, max: 50 };
  }
  if (W < 1280){
    return { min: 100, max: 150 };
  }
  if (W < 1470) {
    return { min: 150, max: 200 };
  }

  
  
  if (W < 1920) {
    return { min: 220, max: 420 };
  }
  return { min: 280, max: 650 };
}

function getMainContentMetrics(headerEl) {
  const main = document.getElementById("mainContent");
  const W = Math.round(headerEl.clientWidth);

  if (!main) {
    return {
      left: CONTENT_PAD,
      right: W - CONTENT_PAD,
      width: W - CONTENT_PAD * 2,
      center: Math.round(W / 2),
    };
  }

  const hRect = headerEl.getBoundingClientRect();
  const mRect = main.getBoundingClientRect();
  const left = Math.round(mRect.left - hRect.left);
  const right = Math.round(mRect.right - hRect.left);
  const width = Math.round(mRect.width);

  return {
    left,
    right,
    width,
    center: Math.round(left + width / 2),
  };
}

function measureCrownHalfWidth(contentWidth, headerEl) {
  const { min, max } = getCrownHalfBounds(headerEl);

  let halfW = Math.max(
    min,
    Math.min(max, Math.round(contentWidth * CROWN_HALF_RATIO))
  );

  const promo = headerEl.querySelector(
    ".header-info-carousel, .header-promo"
  );
  if (promo) {
    const w = promo.getBoundingClientRect().width;
    if (w > 0) {
      halfW = Math.max(
        halfW,
        Math.min(max, Math.round(w / 2) + 24)
      );
    }
  }

  return halfW;
}

function clampCrownSpan(headerEl, bL, bR, metrics) {
  const hRect = headerEl.getBoundingClientRect();
  const pad = 12;
  const { left: contentLeft, right: contentRight, center: centerX } = metrics;

  bL = Math.max(bL, contentLeft + CONTENT_PAD);
  bR = Math.min(bR, contentRight - CONTENT_PAD);

  const logo = headerEl.querySelector(".logoMenubar");
  if (logo) {
    const lr = logo.getBoundingClientRect();
    const logoRight = Math.round(lr.right - hRect.left + pad);
    if (logoRight < centerX) {
      bL = Math.max(bL, logoRight);
    }
  }

  const userCta = headerEl.querySelector(".userCta");
  if (userCta) {
    const ur = userCta.getBoundingClientRect();
    const ctaLeft = Math.round(ur.left - hRect.left - pad);
    if (ctaLeft > centerX) {
      bR = Math.min(bR, ctaLeft);
    }
  }

  if (bR - bL < 80) {
    const half = Math.max(
      40,
      Math.min(
        Math.round((contentRight - contentLeft - CONTENT_PAD * 2) / 4),
        120
      )
    );
    bL = centerX - half;
    bR = centerX + half;
  }

  const drift = centerX - (bL + bR) / 2;
  if (Math.abs(drift) > 1) {
    const nextL = bL + drift;
    const nextR = bR + drift;
    if (
      nextL >= contentLeft + CONTENT_PAD &&
      nextR <= contentRight - CONTENT_PAD
    ) {
      bL = Math.round(nextL);
      bR = Math.round(nextR);
    }
  }

  return [Math.round(bL), Math.round(bR)];
}

export function drawHeaderCrownLine(svgEl, headerEl) {
  if (!svgEl || !headerEl) return;

  const W = Math.round(headerEl.clientWidth);
  const H = Math.round(headerEl.clientHeight);
  if (!W || !H) return;

  const metrics = getMainContentMetrics(headerEl);
  headerEl.style.setProperty(CONTENT_CENTER_VAR, `${metrics.center}px`);

  const isMobile = H <= 72;

  if (isMobile) {
    svgEl.innerHTML = "";
    return;
  }

  const halfW = measureCrownHalfWidth(metrics.width, headerEl);
  let bL = metrics.center - halfW;
  let bR = metrics.center + halfW;
  [bL, bR] = clampCrownSpan(headerEl, bL, bR, metrics);

  const T = isMobile ? 20 : 34;
  const crownLift = isMobile ? 24 : 38;
  const yB = H - 2;
  const yT = H - crownLift;

  const lA = Math.max(0, bL - T);
  const rB = Math.min(W, bR + T);

  const d =
    `M0 ${yB} H ${lA}` +
    ` C ${bL - T * 0.5} ${yB} ${bL - T * 0.5} ${yT} ${bL} ${yT}` +
    ` H ${bR}` +
    ` C ${bR + T * 0.5} ${yT} ${bR + T * 0.5} ${yB} ${rB} ${yB}` +
    ` H ${W}`;

  const green = "#00ff99";
  const gold = "#F9D722";

  svgEl.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svgEl.setAttribute("preserveAspectRatio", "none");
  svgEl.style.width = `${W}px`;
  svgEl.style.height = `${H}px`;
  svgEl.innerHTML =
    '<defs><linearGradient id="gcc-hdr-grad" x1="0" y1="0" x2="1" y2="0">' +
    `<stop offset="0" stop-color="${green}" stop-opacity=".45"/>` +
    `<stop offset="0.5" stop-color="${gold}" stop-opacity="1"/>` +
    `<stop offset="1" stop-color="${green}" stop-opacity=".45"/>` +
    "</linearGradient></defs>" +
    `<path d="${d}" fill="none" stroke="url(#gcc-hdr-grad)" stroke-width="2.2" vector-effect="non-scaling-stroke"/>`;
}

const HeaderCrownLine = ({ isSidebarCollapsed }) => {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const frameRef = useRef(null);

  const redraw = useCallback(() => {
    const headerEl = wrapRef.current?.closest(".headerContent");
    if (!headerEl || !svgRef.current) return;

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      drawHeaderCrownLine(svgRef.current, headerEl);
    });
  }, []);

  useEffect(() => {
    redraw();

    const headerEl = wrapRef.current?.closest(".headerContent");
    const mainEl = document.getElementById("mainContent");

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(redraw);
      if (headerEl) resizeObserver.observe(headerEl);
      if (mainEl) resizeObserver.observe(mainEl);
    }

    const onTransitionEnd = (event) => {
      if (event.propertyName === "margin-left") {
        redraw();
      }
    };
    if (mainEl) {
      mainEl.addEventListener("transitionend", onTransitionEnd);
    }

    window.addEventListener("resize", redraw);
    window.addEventListener("load", redraw);

    const t1 = setTimeout(redraw, 30);
    const t2 = setTimeout(redraw, 260);
    const t3 = setTimeout(redraw, 380);
    const t4 = setTimeout(redraw, 600);

    return () => {
      window.removeEventListener("resize", redraw);
      window.removeEventListener("load", redraw);
      resizeObserver?.disconnect();
      if (mainEl) {
        mainEl.removeEventListener("transitionend", onTransitionEnd);
      }
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [redraw, isSidebarCollapsed]);

  return (
    <div ref={wrapRef} className="header-crown-line" aria-hidden="true">
      <svg ref={svgRef} className="header-crown-line__svg" preserveAspectRatio="none" />
    </div>
  );
};

export default HeaderCrownLine;
